// src/indicators/indicatorMeta.js
import { INDICATOR_OPTIONS } from "./indicatorOptions";
import {
  DEFAULT_Z_SCORE_YEARS,
  formatZScoreYears,
  getZScoreIndicatorLabel,
  isZScoreConfigurable,
  normalizeZScoreIndicatorKey,
} from "./zScoreConfig";

/** Shown on Calculation & Assumptions page for any indicator that uses Z-score. */
export const Z_SCORE_EXPLANATION =
  "Z-score measures how many standard deviations a value is above or below the mean. Formula: Z = (value − mean) / standard deviation. " +
  "It is computed over a user-selected rolling window (calendar years; decimals allowed). Negative Z = below the historical average (e.g. cheaper); positive Z = above average (e.g. richer). " +
  "Rough guide: |Z| > 1 is notable, |Z| > 2 is extreme.";

export const INDICATOR_DESCRIPTIONS = {
  pe_dev: {
    title: INDICATOR_OPTIONS.pe_dev,
    short:
      "Shows how far current P/E is above or below its own 5-year median (in %).",
    intuition:
      "Large negative values mean the stock trades cheaper than its own history; large positive values mean it’s richer than usual.",
    how:
      "Computes forward P/E from NTM EPS, then compares it to a rolling 5-year median and expresses deviation as a percentage, clamped to ±100%.",
    when:
      "Use for mature companies with reasonably stable earnings and a long history. Avoid for early-stage, highly cyclical, or frequently negative-EPS names.",
    pros: [
      "Company-relative valuation (uses its own history, not arbitrary peers)",
      "Simple to interpret: −40% ≈ materially cheaper than usual",
    ],
    cons: [
      "Garbage in, garbage out if EPS estimates are unreliable",
      "Forward P/E can be distorted near recessions / one-off years",
    ],
    pairWith: ["oey", "vmz"],
  },

// ===============================
//  EV / EBITDA YIELD (raw)
// ===============================
ev_ebitda: {
  title: INDICATOR_OPTIONS.ev_ebitda,
  short:
    "EBITDA ÷ enterprise value on each price bar (EV repriced with market cap when statements allow). Higher = more EBITDA per dollar of EV (often cheaper).",
  intuition:
    "Moves with the stock every day while debt, cash, and TTM EBITDA step at filings—similar spirit to an earnings yield on the whole firm.",
  how:
    "When price, income, and balance sheet allow: TTM EBITDA and latest debt and cash are forward-filled; EV = price × diluted shares + debt − cash; yield = EBITDA ÷ EV. Otherwise quarterly metrics from the API are inverted (EBITDA/EV).",
  when:
    "Mature, EBITDA-positive operating businesses. Not meaningful when EBITDA ≤ 0 or data is too thin.",
  pros: ["Capital-structure-aware headline multiple", "Moves daily with price"],
  cons: ["Simplified EV vs vendor models", "Very sensitive when EV is small"],
  pairWith: ["ev_ebitda_z", "pe_ratio", "oey"],
},

ev_ebitda_z: {
  title: INDICATOR_OPTIONS.ev_ebitda_z,
  usesZScore: true,
  short:
    "Same daily EV/EBITDA yield, then z-scored vs a {years} rolling mean and volatility.",
  intuition:
    "Separates “high yield” from “high yield for this stock” so you see extremes relative to this issuer’s recent path.",
  how:
    "Build the raw EV/EBITDA yield series, then apply rolling z-score vs a {years} calendar-year lookback (user-configurable).",
  when:
    "Pair with raw yield or P/E z when you want a company-relative cheap/rich read on operating yield.",
  pros: ["Comparable to other z indicators in the app", "Highlights vs own history", "Flexible lookback window"],
  cons: ["Needs enough yield history for a stable window", "Not meaningful if raw yield is broken"],
  pairWith: ["ev_ebitda", "ps_ratio", "vmz"],
},

// ===============================
//  DIVIDEND YIELD
// ===============================
div_yield: {
  title: INDICATOR_OPTIONS.div_yield,
  short: "Dividend Yield: TTM dividends divided by price.",
  intuition:
    "Higher yield = more income from the investment. But high yield can also mean distress.",
  how:
    "At each cash-flow filing, TTM dividends paid ÷ diluted shares outstanding gives trailing DPS. That DPS is forward-filled until the next filing; on every price bar in the chart, yield = (latest DPS ÷ that bar’s close) × 100 so the line moves with price between dividend updates.",
  when:
    "Use for income-focused strategies and value screens.",
  pros: ["Simple", "Widely used", "Useful for comparing income stocks"],
  cons: ["Can spike artificially when price crashes"],
  pairWith: ["oey", "peg_ratio"],
},

// ===============================
//  DIVIDEND YIELD (Z-SCORE VS 2Y)
// ===============================
div_yield_z: {
  title: INDICATOR_OPTIONS.div_yield_z,
  usesZScore: true,
  short: "Dividend Yield as Z-score vs {years} rolling mean. Z-score > 0 means yield is above its historical average.",
  intuition:
    "When dividend yield is materially above its own recent history, price may be cheaper relative to cash distributions (or the market is pricing stress).",
  how:
    "Computes daily trailing dividend yield (TTM DPS / price), then normalizes using Z-score against a {years} rolling mean and standard deviation (user-configurable).",
  when:
    "Useful for mature dividend payers and income-focused screens.",
  pros: ["Company-relative income valuation", "Updates daily with price", "Flexible lookback window"],
  cons: ["Yield can spike in distress", "Less useful for low/no-dividend companies"],
  pairWith: ["div_yield", "oey", "pb_ratio"],
},

// ===============================
//  COMBINED CLASSIC / NOVEL FUNDAMENTAL
// ===============================
cnf_combo: {
  title: INDICATOR_OPTIONS.cnf_combo,
  short:
    "Equal-weight average of selected Classic/Novel Fundamental Z-score indicators (P/E, P/B, P/S, P/OI, P/CF, dividend yield Z, EV/EBITDA yield Z).",
  intuition:
    "A single composite valuation Z-score. Low values mean the selected metrics are collectively cheaper vs their own history; high values mean richer.",
  how:
    "For each selected member, computes its rolling Z-score (using that indicator’s lookback). Yield-style members (dividend yield Z, EV/EBITDA yield Z) are sign-flipped so low = cheap. Then averages available values at each date with equal weight.",
  when:
    "Use when you want a blended valuation signal instead of relying on one multiple. Deselect members that are noisy or irrelevant for the company.",
  pros: [
    "Diversifies across valuation lenses",
    "Equal weight keeps the blend simple and transparent",
    "Member set is configurable",
  ],
  cons: [
    "Missing data for some members can thin coverage early in history",
    "Equal weight may not match economic importance of each metric",
    "Blending can mute extreme signals from a single strong indicator",
  ],
  pairWith: ["pe_ratio", "pb_ratio", "vmz", "oey"],
},

// ===============================
//  P/B RATIO (Z-SCORE)
// ===============================
pb_ratio: {
  title: INDICATOR_OPTIONS.pb_ratio,
  usesZScore: true,
  short: "Price-to-Book ratio as Z-score vs {years} rolling mean. Z-score < 0 indicates below-average valuation.",
  intuition:
    "A stock with P/B below its historical average may be undervalued relative to its own past.",
  how:
    "Calculates daily P/B (Price / Book Value Per Share), then Z-scores against a {years} rolling mean and standard deviation.",
  when:
    "Useful for financials or asset-heavy companies. Less relevant for software/IP-heavy names.",
  pros: ["Stable metric", "Company-relative valuation", "Configurable lookback"],
  cons: ["Book value can be irrelevant for many modern companies"],
  pairWith: ["vmz", "oey"],
},

// ===============================
//  P/E RATIO (Z-SCORE)
// ===============================
pe_ratio: {
  title: INDICATOR_OPTIONS.pe_ratio,
  usesZScore: true,
  short: "Price-to-Earnings ratio as Z-score vs {years} rolling mean. Z-score < 0 indicates below-average valuation.",
  intuition:
    "A stock with P/E below its historical average may be undervalued relative to its own earnings-based valuation history.",
  how:
    "Calculates daily P/E (Price / TTM EPS), then Z-scores against a {years} rolling mean and standard deviation.",
  when:
    "Useful for companies with stable, positive earnings. Less useful when EPS is negative or highly cyclical.",
  pros: ["Widely understood valuation metric", "Company-relative valuation context"],
  cons: ["Breaks for negative EPS", "Can be noisy for cyclical earnings"],
  pairWith: ["pb_ratio", "vmz", "oey"],
},

// ===============================
//  P/OPERATING INCOME (Z-SCORE VS 2Y)
// ===============================
poi_ratio: {
  title: INDICATOR_OPTIONS.poi_ratio,
  usesZScore: true,
  short: "Price-to-operating-income as Z-score vs {years} rolling mean.",
  intuition:
    "Operating income is a cleaner operating-earnings line than net income for many businesses.",
  how:
    "Price ÷ TTM operating income per share, then Z-scored vs a {years} rolling mean and standard deviation.",
  when:
    "Use for profitable operators where operating income is meaningful.",
  pros: ["Links valuation to operating profitability", "Configurable lookback"],
  cons: ["Omitted when TTM operating income ≤ 0"],
  pairWith: ["pe_ratio", "ev_ebitda_z", "vmz"],
},

// ===============================
//  PEG RATIO
// ===============================
peg_ratio: {
  title: INDICATOR_OPTIONS.peg_ratio,
  short: "Price/Earnings relative to growth. PEG < 1 is traditionally considered 'cheap growth'.",
  intuition:
    "A stock with strong growth and modest P/E yields a lower PEG, signaling undervaluation.",
  how:
    "Forward P/E divided by 3-year EPS CAGR (with floor on growth to avoid division by near-zero numbers).",
  when:
    "Useful for stable-growth companies. Avoid highly cyclical or negative EPS situations.",
  pros: ["Combines value and growth", "Useful for growth-at-a-reasonable-price (GARP)"],
  cons: ["Growth estimates often unreliable", "Breaks when EPS is tiny"],
  pairWith: ["rmo", "ed"],
},

// ===============================
//  P/S RATIO (Z-SCORE VS 2Y)
// ===============================
ps_ratio: {
  title: INDICATOR_OPTIONS.ps_ratio,
  usesZScore: true,
  short: "Price-to-Sales ratio as Z-score vs {years} rolling mean.",
  intuition:
    "A stock with P/S below its historical average may be undervalued relative to its own past.",
  how:
    "Calculates daily P/S (Price / TTM Sales Per Share), then Z-scores against a {years} rolling mean and standard deviation.",
  when:
    "Useful for revenue-generating companies, especially with volatile or negative earnings.",
  pros: ["Works when earnings are negative or volatile", "Configurable lookback"],
  cons: ["Doesn't account for profit margins"],
  pairWith: ["pb_ratio", "vmz", "oey"],
},

// ===============================
//  PRICE/CASH FLOW (Z-SCORE)
// ===============================
cf_price: {
  title: INDICATOR_OPTIONS.cf_price,
  usesZScore: true,
  short: "P/CF as Z-score vs {years} rolling mean. Z-score < 0 = below average.",
  intuition:
    "Uses market cap divided by latest TTM free cash flow; negative z suggests cheaper vs your own history.",
  how:
    "Calculates daily P/CF (Market Cap / TTM Free Cash Flow), then Z-scores against a {years} rolling mean and standard deviation.",
  when:
    "Use for cash-generating companies with consistent free cash flow.",
  pros: ["Cash flow is harder to manipulate than earnings", "Configurable lookback"],
  cons: ["Less useful for growth companies with negative FCF"],
  pairWith: ["oey", "pb_ratio"],
},

// ===============================
//  MEAN REVERSION (PRICE Z)
// ===============================
mean_rev: {
  title: INDICATOR_OPTIONS.mean_rev,
  usesZScore: true,
  short: "Z-score of price vs its 50-day moving average.",
  intuition:
    "Shows how stretched price is above/below its short-term mean.",
  how:
    "Price minus rolling mean divided by rolling standard deviation.",
  when:
    "Use for timing and overbought/oversold conditions in sideways markets.",
  pros: ["Simple", "Good contrarian signal"],
  cons: ["Weak in strong trending markets", "Highly short-term"],
  pairWith: ["vmz", "vmo", "risk"],
},

// ===============================
//  SYO – FUNDAMENTAL YIELD OSCILLATOR
// ===============================
syo: {
  title: INDICATOR_OPTIONS.syo,
  short:
    "Composite of earnings yield, FCF yield, and revenue growth, ranked 0–100 (updates daily).",
  intuition:
    "High values mean strong yield + growth fundamentals relative to the stock's 5-year history.",
  how:
    "Ranks EY, FCFY, and revenue growth independently using a 5-year rolling window, then weights (40% EY, 40% FCFY, 20% Rev_G) and combines. Updates daily as price changes.",
  when:
    "Use for value + quality + growth blends. Daily updates provide timely signals.",
  pros: ["Stable", "Multi-dimensional", "Works across sectors", "Daily updates", "5-year rolling window keeps ranks anchored to a full cycle"],
  cons: ["Self-relative, not peer-relative"],
  pairWith: ["pb_ratio", "oey"],
},

// ===============================
//  RMO – REVENUE MOMENTUM OSCILLATOR
// ===============================
rmo: {
  title: INDICATOR_OPTIONS.rmo,
  short:
    "Looks at whether revenue growth is accelerating or decelerating relative to price.",
  intuition:
    "If revenue grows faster than price, it may indicate undervaluation.",
  how:
    "Compares TTM revenue growth vs price growth over ~4 quarters.",
  when:
    "Use for identifying early improvements in revenue fundamentals.",
  pros: ["Captures early growth inflections"],
  cons: ["Revenue can be lumpy", "Backward-looking"],
  pairWith: ["peg_ratio", "ed"],
},

// ===============================
//  TAMO – TOTAL ASSETS MOMENTUM OSCILLATOR
// ===============================
tamo: {
  title: INDICATOR_OPTIONS.tamo,
  short:
    "Compares quarterly total assets growth vs price growth to identify undervaluation.",
  intuition:
    "If total assets grow faster than price, the company may be undervalued. Similar concept to RMO but using balance sheet assets.",
  how:
    "Compares quarterly total assets growth vs price growth over ~4 quarters (year-over-year).",
  when:
    "Use for identifying companies where asset growth outpaces price appreciation.",
  pros: ["Balance sheet based", "Captures asset accumulation"],
  cons: ["Assets can be inflated by acquisitions", "Backward-looking"],
  pairWith: ["rmo", "pb_ratio", "vmz"],
},

// ===============================
//  NIMO – NET INCOME MOMENTUM OSCILLATOR
// ===============================
nimo: {
  title: INDICATOR_OPTIONS.nimo,
  short:
    "Compares TTM net income growth vs price growth to identify undervaluation.",
  intuition:
    "If net income grows faster than price, the company may be undervalued. Similar concept to RMO but using net income instead of revenue.",
  how:
    "Compares TTM net income growth vs price growth over ~4 quarters (year-over-year).",
  when:
    "Use for identifying companies where profitability growth outpaces price appreciation.",
  pros: ["Profitability based", "Captures earnings momentum"],
  cons: ["Net income can be volatile", "Backward-looking"],
  pairWith: ["rmo", "tamo"],
},

// ===============================
//  VMZ – VALUATION MULTIPLE Z-SCORE
// ===============================
vmz: {
  title: INDICATOR_OPTIONS.vmz,
  short:
    "Z-score of the valuation multiple EV/EBITDA vs 5-year history.",
  intuition:
    "Shows whether valuation is high/low relative to the stock’s own past.",
  how:
    "Computes rolling 5-year median and stdev of valuation multiple and normalizes.",
  when:
    "Use for long-term valuation context.",
  pros: ["Smooth", "Company-specific"],
  cons: ["Regime shifts break the reference window"],
  pairWith: ["vmo", "pb_ratio", "risk"],
},

// ===============================
//  OEY – OWNER EARNINGS YIELD
// ===============================
oey: {
  title: INDICATOR_OPTIONS.oey,
  short: "Free cash flow yield relative to market cap (percentile ranked).",
  intuition:
    "Shows how much value the business returns to shareholders.",
  how:
    "Each bar: TTM free cash flow and the latest diluted share count (forward-filled from filings) are combined with that day’s price to form FCF yield (100×FCF/MCap). The score is the percentile rank of today’s yield within roughly the last five years of prior daily yields, so it updates with price every session and with fundamentals when cash flow or share count changes.",
  when:
    "Use for value investing, dividend alternatives, and quality screens.",
  pros: ["Better than P/E in many sectors"],
  cons: ["FCF volatile in capex-heavy sectors"],
  pairWith: ["pb_ratio", "syo"],
},

// ===============================
//  VMO – VALUATION MOMENTUM (ΔZ)
// ===============================
vmo: {
  title: INDICATOR_OPTIONS.vmo,
  short:
    "First derivative of VMZ: how fast valuation is re-rating.",
  intuition:
    "Positive VMO means markets are paying a richer multiple each period.",
  how:
    "Computes day-to-day change in VMZ.",
  when:
    "Use for detecting re-rating phases.",
  pros: ["Shows momentum in valuation itself"],
  cons: ["Very noisy"],
  pairWith: ["vmz", "risk"],
},

// ===============================
//  MRVZ – REALIZED PRICE Z-SCORE (VWAC)
// ===============================
mrvz: {
  title: INDICATOR_OPTIONS.mrvz,
  usesZScore: true,
  short:
    "Z-score of price relative to VWAC (volume-weighted average cost).",
  intuition:
    "Shows whether most holders are in profit or underwater.",
  how:
    "Computes VWAC over ~252 days, then Z-scores the price/VWAC ratio.",
  when:
    "Use for sentiment and crowd cost basis analysis.",
  pros: ["Intuitive cost-basis proxy"],
  cons: ["VWAC is approximate"],
  pairWith: ["nupl", "vddm", "srm"],
},

// ===============================
//  VDDM – VALUE DAYS DESTROYED METRIC
// ===============================
vddm: {
  title: INDICATOR_OPTIONS.vddm,
  short:
    "Measures cumulative loss intensity across time and volume.",
  intuition:
    "Higher VDDM = deeper, longer periods of holders being underwater.",
  how:
    "Combines drawdown magnitude, duration, and volume.",
  when:
    "Use to detect capitulation or pain accumulation.",
  pros: ["Encodes multiple forms of stress"],
  cons: ["Complex and harder to interpret"],
  pairWith: ["nupl", "mrvz", "risk"],
},

// ===============================
//  NUPL
// ===============================
nupl: {
  title: INDICATOR_OPTIONS.nupl,
  short:
    "Net unrealized profit/loss proxy using VWAC cost basis.",
  intuition:
    "Positive = majority holders in profit; negative = underwater.",
  how:
    "NUPL = (price − realized price) / price.",
  when:
    "Use for sentiment regime mapping.",
  pros: ["Clean psychological indicator"],
  cons: ["VWAC approximation"],
  pairWith: ["mrvz", "vddm", "srm"],
},

// ===============================
//  REALIZED PRICE (VWAC)
// ===============================
realized_price_252: {
  title: INDICATOR_OPTIONS.realized_price_252,
  short: "Volume-weighted average cost over the last 252 trading days (~1 year).",
  intuition: "Approximates the average price paid by recent holders; price below this line suggests aggregate underwater positioning.",
  how: "Rolling VWAC on the price chart (green rising / red falling). Bottom pane: % change per bar of realized price (Δ).",
  when: "Use as a short-term cost-basis support/resistance level alongside spot price.",
  pros: ["Intuitive cost-basis line", "Comparable to Bitcoin realized price concept"],
  cons: ["VWAC proxy, not true on-chain cost basis", "Needs volume data"],
  pairWith: ["nupl", "mrvz", "vddm"],
},
realized_price_504: {
  title: INDICATOR_OPTIONS.realized_price_504,
  short: "Volume-weighted average cost over the last 504 trading days (~2 years).",
  intuition: "A slower-moving cost basis that reflects a broader holder cohort than the 1-year line.",
  how: "Rolling VWAC on the price chart (green rising / red falling). Bottom pane: % change per bar of realized price (Δ).",
  when: "Use for medium-term sentiment and support when 252d feels too reactive.",
  pros: ["Smoother than 252d", "Captures more of a full market cycle"],
  cons: ["VWAC proxy", "Lags sharp repricing events"],
  pairWith: ["realized_price_252", "nupl", "mrvz"],
},
realized_price_5y: {
  title: INDICATOR_OPTIONS.realized_price_5y,
  short: "Volume-weighted average cost over the last 1,260 trading days (~5 years).",
  intuition: "Long-run aggregate acquisition cost; spot well above implies broad unrealized gains.",
  how: "Rolling VWAC on the price chart (green rising / red falling). Bottom pane: % change per bar of realized price (Δ).",
  when: "Use as a structural cost-basis anchor across full market cycles.",
  pros: ["Very stable long-term reference", "Useful for cycle-level context"],
  cons: ["Slow to react", "VWAC proxy"],
  pairWith: ["realized_price_504", "nupl", "srm"],
},

// ===============================
//  RISK (COMPOSITE)
// ===============================
risk: {
  title: INDICATOR_OPTIONS.risk,
  short:
    "Composite risk score (0-100) combining valuation, momentum, mean-reversion, drawdown, and volatility.",
  intuition:
    "Low values (0-30) = good buying opportunities (undervalued, low momentum risk). High values (70-100) = time to sell (overvalued, stretched, high risk).",
  how:
    "Weighted blend: 35% VMZ (valuation), 30% VMO (momentum), 25% mean-reversion Z, 10% drawdown (SRM), adjusted by volatility regime. Uses rolling percentile normalization for adaptive scaling.",
  when:
    "Entry: Risk < 25-30 (excellent to good buying zone). Exit: Risk ≥ 60-70 (elevated to high risk). Use for position sizing and timing entries/exits.",
  pros: [
    "Multi-dimensional risk assessment",
    "Adaptive percentile normalization",
    "Volatility regime adjustment",
    "Multi-timeframe analysis",
    "Captures both valuation and momentum risk"
  ],
  cons: ["Not a perfect timing tool", "May lag during rapid market changes"],
  pairWith: ["srm", "mrvz", "vddm", "nupl"],
  thresholds: {
    entry: {
      excellent: "< 25",
      good: "< 30",
      moderate: "< 35"
    },
    exit: {
      consider: "≥ 50",
      strong: "≥ 60",
      aggressive: "≥ 70",
      extreme: "≥ 80"
    }
  }
},

// ===============================
//  SRM – STRESS RISK METRIC
// ===============================
srm: {
  title: INDICATOR_OPTIONS.srm,
  short:
    "Percentile rank of max drawdown intensity over trailing window.",
  intuition:
    "High SRM = high market stress; low SRM = calm conditions.",
  how:
    "Computes rolling max drawdown and normalizes across 5-year history.",
  when:
    "Use for risk, volatility, and trend stress detection.",
  pros: ["Intuitive", "Works across sectors"],
  cons: ["Lagging indicator"],
  pairWith: ["risk", "nupl", "mrvz"],
},

// ===============================
//  P/E Ratio Comparison (Historical/Forward)
// ===============================
pe_peratio: {
  title: INDICATOR_OPTIONS.pe_peratio,
  short:
    "Compares historical P/E ratio to forward P/E ratio to identify valuation opportunities.",
  intuition:
    "When the ratio is above 1, the stock's historical P/E (based on actual earnings) is higher than its forward P/E (based on analyst estimates), suggesting the stock may be undervalued relative to expected earnings growth. When below 1, forward P/E exceeds historical P/E, indicating potential overvaluation.",
  how:
    "Calculates (Average price last year / Average actual EPS last year) divided by (Current price / Average next year EPS estimates). Uses the last 4 quarters of actual EPS and next 4 quarters of analyst EPS estimates.",
  when:
    "Use when evaluating whether current valuation is attractive relative to both historical norms and analyst expectations. Best for companies with consistent earnings and reliable analyst coverage.",
  pros: [
    "Combines historical and forward-looking perspectives",
    "Identifies valuation opportunities relative to earnings expectations",
    "Works well for established companies with analyst coverage"
  ],
  cons: [
    "Depends on accuracy of analyst estimates",
    "Requires sufficient historical data (at least 4 quarters)",
    "Less reliable for companies with volatile or negative earnings"
  ],
  pairWith: ["peg_ratio", "pe_dev", "vmo", "forward_pe"],
},

// ===============================
//  Forward P/E (NTM consensus)
// ===============================
forward_pe: {
  title: INDICATOR_OPTIONS.forward_pe,
  short: "Price divided by next-twelve-months (NTM) EPS — sum of the next four quarterly EPS estimates when available.",
  intuition:
    "A lower forward multiple means you pay less per dollar of expected near-term earnings; it moves with price every session while the estimate ladder updates with filings and consensus.",
  how:
    "For each price bar, collects up to four forward quarterly EPS estimates (consensus next4 when the bar is near the latest reported quarter, otherwise estimates from earnings history). NTM EPS ≈ sum of those four quarters. Forward P/E = price ÷ NTM EPS, consistent with typical sites that use price ÷ forward annual or next-12-month EPS (not price ÷ average quarterly EPS).",
  when:
    "Best for profitable names with analyst coverage and sensible positive estimates. Treat with caution when estimates are stale, sparse, or dominated by one-offs.",
  pros: ["Simple, widely recognized multiple", "Forward-looking vs trailing P/E"],
  cons: ["Estimate quality varies", "Less meaningful when EPS is near zero or negative"],
  pairWith: ["pe_ratio", "pe_peratio", "peg_ratio", "ttm_pe"],
},

// ===============================
//  TTM P/E (trailing EPS)
// ===============================
ttm_pe: {
  title: INDICATOR_OPTIONS.ttm_pe,
  short: "Price divided by trailing-twelve-months EPS — sum of the last four reported quarterly EPS values.",
  intuition:
    "A lower TTM multiple means you pay less per dollar of earnings already reported over the past year; it updates on each price bar and steps when new quarters are filed.",
  how:
    "For each price bar, collects the four most recent quarters with positive actual EPS reported on or before that bar. TTM EPS = sum of those four quarters. TTM P/E = price ÷ TTM EPS (same denominator convention as Forward P/E, but using actuals instead of estimates).",
  when:
    "Best for profitable names with consistent quarterly reporting. Less meaningful when EPS is negative or lumpy from one-offs.",
  pros: ["Simple, widely recognized multiple", "Based on reported earnings, not estimates"],
  cons: ["Backward-looking vs forward P/E", "Can lag sudden earnings changes until filings"],
  pairWith: ["forward_pe", "pe_ratio", "pe_peratio", "peg_ratio"],
},

revenue_cagr: {
  title: INDICATOR_OPTIONS.revenue_cagr,
  short: "Historical 1y/2y TTM revenue CAGR plus implied 1y growth from analyst NTM estimates.",
  intuition:
    "Compare reported growth (blue/red) to the green forward line (TTM today vs sum of next four quarter revenue estimates). When forward est sits above historical lines, the street expects re-acceleration.",
  how:
    "TTM revenue from the last four reported quarters. Historical: CAGR vs ~1y and ~2y ago TTM. Forward (dotted green): (NTM revenue estimate ÷ current TTM) − 1, using analyst next4 when available (same sourcing as Forward P/E).",
  when:
    "Useful for growth companies with stable, positive revenue. Less meaningful after large acquisitions/divestitures or when historical revenue was near zero.",
  pros: ["Standard growth metric", "Based on reported revenue"],
  cons: ["Backward-looking", "Sensitive to base-year effects"],
  pairWith: ["eps_cagr", "rmo", "peg_ratio", "ps_ratio"],
},

eps_cagr: {
  title: INDICATOR_OPTIONS.eps_cagr,
  short: "Historical 1y/2y TTM EPS CAGR plus implied 1y growth from analyst NTM estimates.",
  intuition:
    "Compare reported EPS growth to the green forward line (current TTM EPS vs sum of next four quarter EPS estimates). Useful with P/E and PEG when judging whether growth expectations are rising.",
  how:
    "TTM EPS from the last four reported quarters. Historical: CAGR vs ~1y and ~2y ago. Forward (dotted green): (NTM EPS estimate ÷ current TTM EPS) − 1, using analyst next4 when available. Requires positive TTM and estimate sums.",
  when:
    "Best when EPS is positive and not dominated by one-offs. Avoid for chronic losers or highly distorted periods.",
  pros: ["Reported-earnings based", "More responsive than longer horizons"],
  cons: ["EPS volatility / negative stretches produce gaps"],
  pairWith: ["revenue_cagr", "peg_ratio", "forward_pe", "ttm_pe"],
},

fgpz: {
  title: INDICATOR_OPTIONS.fgpz,
  short: "Z-score of how rich or cheap forward P/E is vs this stock's own growth–multiple curve.",
  intuition:
    "High-growth names usually trade on higher forward P/E. FGPZ asks whether today's forward multiple is above or below what this stock's history implies, given both trailing EPS growth and analyst forward growth. Negative = growth may be under-priced; positive = multiple may already reflect optimism.",
  how:
    "Fits ln(Forward P/E) ~ trailing 1y EPS CAGR + forward NTM EPS growth on a rolling 7y history (out-of-sample residual each bar). FGPZ is the 2y z-score of that residual. Green when FGPZ < −1, red when > +1.",
  when:
    "Best for profitable names with analyst coverage and stable EPS. Weak when EPS is negative, estimates are stale, or growth is extremely volatile.",
  pros: ["Stock-specific growth–multiple curve", "Combines past performance and forward consensus", "Z-score aids mean-reversion timing"],
  cons: ["Depends on estimate quality", "Needs ~1y+ of aligned growth/multiple history", "Less meaningful for pre-profit or lumpy earners"],
  pairWith: ["gag", "peg_ratio", "forward_pe", "eps_cagr", "pe_peratio"],
},

gag: {
  title: INDICATOR_OPTIONS.gag,
  short: "Whether expected EPS re-acceleration is already reflected in the P/E compression from trailing to forward.",
  intuition:
    "When analysts expect faster growth (forward NTM > trailing CAGR) but the multiple hasn't compressed much (TTM P/E ≈ Forward P/E), growth may still be under-priced — positive GAG. When the multiple already compressed ahead of growth, GAG turns negative.",
  how:
    "GAG = (forward NTM EPS growth % − trailing 1y EPS CAGR %) − 100×(TTM P/E ÷ Forward P/E − 1). Green when GAG > 0, red when < 0.",
  when:
    "Use alongside FGPZ and PEG when judging whether growth expectations are already in the price. Requires positive TTM and forward EPS.",
  pros: ["Intuitive re-acceleration vs multiple gap", "Updates with price and consensus", "Complements FGPZ without z-scoring"],
  cons: ["Sensitive to estimate revisions", "Noisy when forward P/E is very low or very high", "EPS-only (not revenue)"],
  pairWith: ["fgpz", "eps_cagr", "forward_pe", "pe_peratio", "peg_ratio"],
},

rev_val: {
  title: INDICATOR_OPTIONS.rev_val,
  short:
    "Price divided by revenue-based fair value. Below 1 = undervalued, above 1 = overvalued. Green when price is below 95% CI, red when above.",
  intuition:
    "Uses linear regression of historical (revenue, price) to predict fair value from analyst revenue forecasts. Compares current price to that prediction.",
  how:
    "Model built on at least 8 quarters of actual revenue vs price. Next-quarter analyst revenue forecast feeds the model for predicted price. Indicator = current price / predicted price. 95% confidence interval for shading.",
  when:
    "Use for revenue-driven companies where revenue correlates with price. Works best with consistent analyst coverage.",
  pros: [
    "Uses same methodology as price forecast",
    "Clear undervalued/overvalued signal",
    "95% CI bands for confidence",
  ],
  cons: [
    "Assumes revenue–price relationship is stable",
    "Depends on analyst forecast quality",
  ],
  pairWith: ["peg_ratio", "pe_dev", "pe_peratio"],
},

eps_val: {
  title: INDICATOR_OPTIONS.eps_val,
  short:
    "Price divided by EPS-based fair value. Below 1 = undervalued, above 1 = overvalued. Green when price is below 95% CI, red when above.",
  intuition:
    "Uses linear regression of historical (EPS, price) to predict fair value from analyst EPS forecasts. Compares current price to that prediction.",
  how:
    "Model built on at least 8 quarters of actual EPS vs price. Next-quarter analyst EPS forecast feeds the model for predicted price. Indicator = current price / predicted price. 95% confidence interval for shading.",
  when:
    "Use for earnings-driven companies where EPS correlates with price. Works best with consistent analyst coverage.",
  pros: [
    "Uses same methodology as revenue valuation but for earnings",
    "Clear undervalued/overvalued signal",
    "95% CI bands for confidence",
  ],
  cons: [
    "Assumes EPS–price relationship is stable",
    "Depends on analyst forecast quality",
    "Less useful when EPS is negative or very volatile",
  ],
  pairWith: ["rev_val", "peg_ratio", "pe_peratio"],
},
};

// Safe accessor so missing descriptions don’t explode the UI
export function getIndicatorDescription(key, options = {}) {
  const baseKey = normalizeZScoreIndicatorKey(key);
  const meta = INDICATOR_DESCRIPTIONS[baseKey] || INDICATOR_DESCRIPTIONS[key];
  const zScoreYears = options.zScoreYears ?? DEFAULT_Z_SCORE_YEARS;
  const yearsLabel = formatZScoreYears(zScoreYears);

  if (!meta) {
    const title = isZScoreConfigurable(baseKey)
      ? getZScoreIndicatorLabel(baseKey, zScoreYears)
      : (INDICATOR_OPTIONS[key] || key);
    return {
      title,
      short: "Description coming soon.",
      intuition: "",
      how: "",
      when: "",
      pros: [],
      cons: [],
      pairWith: [],
      usesZScore: isZScoreConfigurable(baseKey),
    };
  }

  if (!meta.usesZScore) return meta;

  const applyYears = (text) =>
    typeof text === "string"
      ? text.replace(/\{years\}/g, yearsLabel)
      : text;

  return {
    ...meta,
    title: getZScoreIndicatorLabel(baseKey, zScoreYears),
    short: applyYears(meta.short),
    intuition: applyYears(meta.intuition),
    how: applyYears(meta.how),
    when: applyYears(meta.when),
    pros: (meta.pros || []).map(applyYears),
    cons: (meta.cons || []).map(applyYears),
    pairWith: meta.pairWith || [],
    usesZScore: true,
  };
}
